import { useEffect, useLayoutEffect, useMemo } from "react";
import { View } from "react-native";
import { CommonActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useLocale } from "@src/packages/i18n";
import type { AccessStackParamList } from "../../types";
import { ConditionDetailContent } from "../../components/ConditionDetailSheet";
import { getFactSheetRouteBlock } from "../../components/factSheetRouteIds";
import type { FactSheetLinkTarget } from "../../components/LinkedFactSheetText";
import { getTestRouteTarget } from "./testRouteIds";
import { useSmartBack } from "../../hooks/useSmartBack";

type Props = NativeStackScreenProps<AccessStackParamList, "ConditionDetail">;

export default function ConditionDetailScreen({ route, navigation }: Props) {
  const { conditionId, returnTab } = route.params;
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });

  const condition = useMemo(() => {
    void locale;
    return getFactSheetRouteBlock("condition", conditionId);
  }, [conditionId, locale]);

  useEffect(() => {
    if (!condition) {
      navigation.goBack();
    }
  }, [condition, navigation]);

  useLayoutEffect(() => {
    if (condition) {
      navigation.setOptions({ title: condition.title });
    }
  }, [condition, navigation]);

  if (!condition) return null;

  const openFactSheetLink = (target: FactSheetLinkTarget) => {
    switch (target.kind) {
      case "condition":
        navigation.navigate("ConditionDetail", { conditionId: target.id });
        return;
      case "supplement":
        navigation.navigate("SupplementDetail", { supplementId: target.id });
        return;
      case "test":
        {
          const testTarget = getTestRouteTarget(target.id);
          if (testTarget.screen === "TestProductDetail") {
            navigation.navigate("TestProductDetail", testTarget.params);
            return;
          }
          navigation.navigate("TestDetail", testTarget.params);
        }
        return;
      case "therapy":
        navigation.navigate("TherapyDetail", { therapyId: target.id });
        return;
      case "medication":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Learn",
            params: {
              screen: "MedicationDetail",
              params: { medicationId: target.id },
            },
          }),
        );
        return;
      case "procedure":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Learn",
            params: {
              screen: "ProcedureDetail",
              params: { procedureId: target.id },
            },
          }),
        );
        return;
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <ConditionDetailContent
        item={condition}
        onClose={handleBack}
        onOpenFactSheet={openFactSheetLink}
        skipExitAnimation
      />
    </View>
  );
}
